import { Button } from '@/design-system';

import { isApiError } from '../api/errors';
import { describeError } from './error-reporting';

export interface CrashPanelProps {
    error: unknown;
    /** De React-componentstack; staat mee in de uitklapper. */
    componentStack?: string | null;
    /** Rendert alleen dit stuk opnieuw. Geen herlaad: offline werk mag niet verdampen. */
    onRetry: () => void;
    /** Waar de fout vandaan kwam; staat mee in de uitklapper. */
    scope?: string;
}

/**
 * Wat de gebruiker ziet als een scherm vastloopt.
 *
 * Drie dingen, in deze volgorde: wat er misging, wat je nu kunt doen, en pas
 * daarna - dichtgeklapt - de techniek. Een kale witte pagina met een stacktrace
 * is voor een keurmeester op een dak geen melding maar een dood apparaat.
 *
 * "Opnieuw proberen" rendert alleen dit deel van de boom opnieuw. Bewust géén
 * `location.reload()`: een herlaad gooit lopende opnames en de outbox-status weg
 * en dat is precies het werk dat niet verloren mag gaan.
 */
export function CrashPanel({ error, componentStack, onRetry, scope }: CrashPanelProps) {
    const offline = isApiError(error) && error.kind === 'offline';

    return (
        <div className="flex min-h-0 flex-1 items-start justify-center overflow-auto bg-[var(--surface-page)] px-7 py-8">
            <section
                role="alert"
                aria-labelledby="sk-crash-title"
                className="w-full max-w-[560px] rounded-[var(--radius-card)] border border-[var(--border-default)] bg-[var(--surface-card)] p-[18px]"
            >
                <span
                    aria-hidden="true"
                    className="mb-[10px] block h-[3px] w-8 rounded-[2px]"
                    style={{ background: offline ? 'var(--warn-field)' : 'var(--danger)' }}
                />

                <h1
                    id="sk-crash-title"
                    className="m-0 font-[family-name:var(--font-display)] text-[24px] leading-[1.15] font-semibold tracking-[-0.02em] text-[var(--text-primary)]"
                >
                    {offline ? 'Geen verbinding' : 'Dit scherm liep vast'}
                </h1>

                <p className="mt-[10px] mb-0 text-[14px] leading-[1.5] text-[var(--text-reading)]">
                    {offline
                        ? 'Dit scherm heeft gegevens nodig die nog niet op dit apparaat staan. Zodra je weer verbinding hebt, werkt het gewoon.'
                        : 'Er ging iets mis bij het tonen van dit scherm. Je werk is niet weg: wat je hebt ingevuld staat op dit apparaat en gaat mee zodra er verbinding is.'}
                </p>

                <p className="mt-2 mb-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    Probeer het opnieuw. Blijft het misgaan, ga dan via het menu links naar een
                    ander scherm en geef de technische details door.
                </p>

                <div className="mt-[14px] flex flex-wrap gap-2">
                    <Button variant="primary" size="compact" onClick={onRetry}>
                        Opnieuw proberen
                    </Button>
                </div>

                <details className="mt-[14px] border-t border-[var(--border-subtle)] pt-3">
                    <summary className="cursor-pointer list-none text-[13px] text-[var(--text-secondary)]">
                        Technische details
                    </summary>

                    <pre className="mt-[10px] mb-0 max-h-[240px] overflow-auto rounded-[var(--radius-control-sm)] bg-[var(--surface-subtle)] p-3 font-[family-name:var(--font-mono)] text-[11px] leading-[1.5] whitespace-pre-wrap text-[var(--text-secondary)]">
                        {[
                            scope ? `scope: ${scope}` : null,
                            describeError(error),
                            error instanceof Error ? error.stack : null,
                            componentStack,
                        ]
                            .filter(Boolean)
                            .join('\n\n')}
                    </pre>
                </details>
            </section>
        </div>
    );
}
